// The usage summary counts dependents to the API's LINEAGE_MAX_TRAVERSAL_DEPTH,
// so every export walks the same depth and lists the resources the summary counts.
// Not imported from the orchestration constants, which the app bundle cannot load.
export const LINEAGE_EXPORT_MAX_DEPTH = 10;

export const DEPENDENCY_EXPORT_HEADERS = [
  "Root Resource ID",
  "Root Resource Type",
  "Root Resource Name",
  "Root Resource URL",
  "Dependent Resource ID",
  "Dependent Resource Type",
  "Dependent Resource Name",
  "Dependent Resource URL",
  "Depth",
  "Dependency Via ID",
  "Dependency Via Type",
  "Dependency Via Name",
  "Dependency Via URL",
];

export type LineageExportRef = {
  resourceType: string;
  resourceId: string;
};

export type LineageExportNamedRef = LineageExportRef & {
  name: string | null;
};

/** What an export knows about the resource its dependents hang off. */
export type LineageExportRoot = LineageExportNamedRef & {
  /** Empty when nothing addresses the root. */
  url: string;
};

export type LineageDependent<TNode> = {
  node: TNode;
  /** False lets the walk skip reading a resource nothing depends on. */
  hasDependents: boolean;
};

/**
 * Reads the direct dependents of each parent, in the order the parents were
 * given. A walk can read from an in-memory graph or page through the API.
 */
export type ReadLineageDependents<TNode> = (
  parents: readonly LineageExportRef[],
) =>
  | ReadonlyArray<ReadonlyArray<LineageDependent<TNode>>>
  | Promise<ReadonlyArray<ReadonlyArray<LineageDependent<TNode>>>>;

export type ReachedDependent<TNode> = {
  node: TNode;
  /** Edges walked from the root, so a direct dependent is 1. */
  depth: number;
  /** Null for a direct dependent, which is reached through nothing. */
  via: TNode | null;
};

export function lineageRefKey({
  resourceType,
  resourceId,
}: LineageExportRef): string {
  return `${resourceType}:${resourceId}`;
}

export function compareLineageNodes(
  a: LineageExportNamedRef,
  b: LineageExportNamedRef,
): number {
  return (
    a.resourceType.localeCompare(b.resourceType) ||
    (a.name ?? "").localeCompare(b.name ?? "") ||
    a.resourceId.localeCompare(b.resourceId)
  );
}

/**
 * Every resource downstream of the root, walked breadth first so each one is
 * reported once, at its shortest depth and through the resource on that path.
 * Sorted by depth, then type, name, and ID.
 */
export async function collectLineageDependents<
  TNode extends LineageExportNamedRef,
>({
  root,
  readDependents,
  maxDepth = LINEAGE_EXPORT_MAX_DEPTH,
}: {
  root: LineageExportRef;
  readDependents: ReadLineageDependents<TNode>;
  maxDepth?: number;
}): Promise<ReachedDependent<TNode>[]> {
  const visited = new Set<string>([lineageRefKey(root)]);
  const reached: ReachedDependent<TNode>[] = [];
  let parents: Array<{ ref: LineageExportRef; node: TNode | null }> = [
    { ref: root, node: null },
  ];

  for (let depth = 1; depth <= maxDepth && parents.length > 0; depth += 1) {
    const dependentsByParent = await readDependents(
      parents.map((parent) => parent.ref),
    );

    const next: typeof parents = [];
    parents.forEach((parent, index) => {
      for (const dependent of dependentsByParent[index] ?? []) {
        const key = lineageRefKey(dependent.node);
        if (visited.has(key)) {
          continue;
        }
        visited.add(key);
        reached.push({ node: dependent.node, depth, via: parent.node });
        if (dependent.hasDependents) {
          next.push({ ref: dependent.node, node: dependent.node });
        }
      }
    });
    parents = next;
  }

  return reached.sort(
    (a, b) => a.depth - b.depth || compareLineageNodes(a.node, b.node),
  );
}

/** One row per dependent, in DEPENDENCY_EXPORT_HEADERS order. */
export function buildDependencyRows<TNode extends LineageExportNamedRef>({
  root,
  dependents,
  resourceUrl,
}: {
  root: LineageExportRoot;
  dependents: readonly ReachedDependent<TNode>[];
  /** Absolute link to a resource, or an empty string when nothing addresses it. */
  resourceUrl: (node: TNode) => string;
}): string[][] {
  return dependents.map(({ node, depth, via }) => [
    root.resourceId,
    root.resourceType,
    root.name ?? "",
    root.url,
    node.resourceId,
    node.resourceType,
    node.name ?? "",
    resourceUrl(node),
    String(depth),
    via?.resourceId ?? "",
    via?.resourceType ?? "",
    via?.name ?? "",
    via ? resourceUrl(via) : "",
  ]);
}
